/**
 * Playlist Track Utils
 *
 * @module lib/PlaylistTrackUtils
 */
import { fromJS, List } from 'immutable';
import isString from 'lodash/isString';
import memoize from 'lodash/memoize';

import { LyricsCountries } from '../constants/CountryCodes';
import { TrackType } from '../constants/TrackType';
import { getInDomain } from '../domains/externalUtils';
import { getCountryCode } from '../lib/configuration/TunerVarUtils';

import { getTrackById, getTrackEntitiesFromState } from './EntityUtils';

export { getTrackById };
export { getTrackEntitiesFromState };

/**
 * returns true if track is thumbed up.
 *
 * @param  {Map} track - track
 * @returns {boolean} - track thumbed up?
 */
export function isTrackThumbedUp(track) {
  return track.get('rating') > 0;
}

/**
 * returns true if track is thumbed down.
 *
 * @param  {Map} track - track
 * @returns {boolean} - track thumbed down?
 */
export function isTrackThumbedDown(track) {
  return track.get('rating') < 0;
}

export function getThumbedUpDate(track) {
  return (
    track.getIn(['tprFirstThumbed', 'timestamp']) || track.getIn(['tprLastThumbed', 'timestamp'])
  );
}

export function getThumbprintStationId(track) {
  return (
    track.getIn(['tprFirstThumbed', 'stationId']) || track.getIn(['tprLastThumbed', 'stationId'])
  );
}

export function getLyricSnippet(track, state) {
  const lyricSnippet = track.get('lyricSnippet');
  const isLyricsCountry = LyricsCountries.has(getCountryCode(state));
  return lyricSnippet && isLyricsCountry ? lyricSnippet : null;
}

export function getLyricSnippetAPS(state) {
  const lyricSnippet = getInDomain('playback.selectors.getCurrentItemLyricSnippet', '')(state);
  const isLyricsCountry = LyricsCountries.has(getCountryCode(state));

  return lyricSnippet && isLyricsCountry ? lyricSnippet : null;
}

export function getTrackType(track) {
  // tracks served from getFragment have a 'trackType' key, but those served from APS have a nested 'sessionItem' with a 'type'
  return track ? track.get('trackType', track.getIn(['sessionItem', 'type'], null)) : null;
}

export function isAudioWarning(track) {
  return getTrackType(track) === TrackType.AUDIO_WARNING;
}

export function isArtistMessage(track) {
  return getTrackType(track) === TrackType.ARTIST_MESSAGE;
}

export function getArtistMessageDeliveryType(track) {
  return track && isArtistMessage(track) && track.getIn(['artistMessage', 'deliveryType'], null);
}

export function isRegularTrack(track) {
  return getTrackType(track) === TrackType.TRACK;
}

export function isCustomTrack(track) {
  return getTrackType(track) === TrackType.CUSTOM_TRACK;
}

export function isRegularOrCustomTrack(track) {
  return isRegularTrack(track) || isCustomTrack(track);
}

export function getArtistMessageId(track) {
  return track.getIn(['artistMessage', 'artistMessageId']);
}

export function getArtistUid(track) {
  return track.getIn(['artistMessage', 'artistUid']);
}

export function getArtistMessageButtonURL(track) {
  return track.getIn(['artistMessage', 'buttonURL']);
}

export function getArtistMessageButtonText(track) {
  return track.getIn(['artistMessage', 'buttonText']);
}

export function getTrackArtist(track) {
  return track ? track.get('artistName') : '';
}

export function getTrackRights(track, field) {
  const rights = (track && track.get('rights')) || [];
  return rights.indexOf(field) > -1;
}

export function getTrackTemplatePath(track) {
  return track ? track.get('templatePath') : null;
}

export function getTrackAudioToken(track) {
  return track ? track.get('audioTokenId') : null;
}

export function getTrackMusicId(track) {
  return track ? track.get('musicId') : null;
}

export function getTrackAudioEncoding(track) {
  return track ? track.get('audioEncoding') : null;
}

export function getTrackStationId(track) {
  return track ? track.get('stationId') : null;
}

export function isTrackReplayableWithoutReward(track) {
  return !getTrackRights(track, 'replayRequiresReward');
}

export function doesTrackAllowSkipAfterLimit(track) {
  return getTrackRights(track, 'allowSkipAfterLimit');
}

export function getRightsShowReplayButton(track) {
  return getTrackRights(track, 'showReplayButton');
}

export function doesTrackHaveReplayRights(track) {
  return getTrackRights(track, 'allowReplay');
}

export function doesTrackAllowLimitlessSkips(track) {
  return track && track.get('allowSkipTrackWithoutLimit');
}

// We want getEnabledPlaybackControls to return identical immutable objects when
// the set of enabled controls is the same, this will prevent unnecessary rerenders
// that would otherwise trigger because `List() !== List()`
// (see https://medium.com/@esamatti/react-js-pure-render-performance-anti-pattern-fb88c101332f#.lsj5y44w3)
export const controlsMemoizationHelper = memoize(object => {
  let returnVal = object;
  if (isString(object)) {
    returnVal = object.split(',');
  }
  return fromJS(returnVal) || List();
});

const SESSION_HISTORY_TYPES = List([
  TrackType.TRACK,
  TrackType.ARTIST_MESSAGE,
  TrackType.VOICE_TRACK,
  TrackType.CUSTOM_TRACK,
]);

// Only certain tracks should persist in session history
// Others only get played once and aren't stored
export function shouldTrackPersistInSessionHistory(track) {
  if (!track) {
    return false;
  }
  return SESSION_HISTORY_TYPES.includes(getTrackType(track));
}

export function shouldTypePersistInSessionHistory(type) {
  if (!type) return false;
  return SESSION_HISTORY_TYPES.includes(type);
}

export function getArtistAudioMessagesInCurrentPlaylist(state) {
  const id = getInDomain('playback.utils.getCurrentPlaylist')(state);
  const playlistTrackTokens = getInDomain('playback.utils.getPlaylistTrackIds')(state, id);
  return playlistTrackTokens
    .filter(playlistTrackToken => {
      const playlistTrack = getTrackById(state, playlistTrackToken);
      return playlistTrack && isArtistMessage(playlistTrack);
    })
    .toSet();
}
